import type {
  DeliveryMethod,
  OrderStatus,
  PaymentType,
} from "@/modules/orders/domain/types";

type BadgeVariant =
  | "default"
  | "secondary"
  | "destructive"
  | "outline"
  | "success";

export const ORDER_STATUS_META: Record<
  OrderStatus,
  { label: string; variant: BadgeVariant }
> = {
  draft: { label: "ຮ່າງ", variant: "outline" },
  paid: { label: "ຊຳລະແລ້ວ", variant: "success" },
  ready_to_ship: { label: "ພ້ອມສົ່ງ", variant: "secondary" },
  shipped: { label: "ສົ່ງແລ້ວ", variant: "default" },
  delivered: { label: "ຂົນສົ່ງສົ່ງຮອດແລ້ວ", variant: "secondary" },
  picked_up: { label: "ລູກຄ້າໄປເອົາແລ້ວ", variant: "success" },
  returned: { label: "ຂົນສົ່ງຕີກັບ", variant: "destructive" },
  cancelled: { label: "ຍົກເລີກ", variant: "destructive" },
};

/** ສະຖານະ paid + COD = ຢືນຢັນແລ້ວ ຍັງບໍ່ໄດ້ຮັບເງິນ */
const COD_PAID_STATUS_META = {
  label: "ຢືນຢັນ COD",
  variant: "secondary" as BadgeVariant,
};

export function getOrderStatusMeta(
  status: OrderStatus,
  paymentType?: PaymentType,
): { label: string; variant: BadgeVariant } {
  if (status === "paid" && paymentType === "cod") {
    return COD_PAID_STATUS_META;
  }
  return ORDER_STATUS_META[status] ?? { label: status, variant: "outline" };
}

export function orderStatusLabel(
  status: OrderStatus,
  paymentType?: PaymentType,
): string {
  return getOrderStatusMeta(status, paymentType).label;
}

/** ບໍລິສັດຂົນສົ່ງ (ໃຊ້ເມື່ອ delivery_method = carrier) */
export const SHIPPING_CARRIERS = [
  "ຮຸ່ງອາລຸນ",
  "ອານຸສິດ",
  "Mixay Express",
  "HAL Logistics",
  "ອື່ນໆ",
];

export const DELIVERY_METHOD_META: Record<
  DeliveryMethod,
  { label: string; shortLabel: string; variant: BadgeVariant; hint: string }
> = {
  carrier: {
    label: "ສົ່ງຜ່ານຂົນສົ່ງ",
    shortLabel: "ຂົນສົ່ງ",
    variant: "outline",
    hint: "ເລືອກບໍລິສັດຂົນສົ່ງ ແລະ ສາຂາປາຍທາງ",
  },
  pickup: {
    label: "ລູກຄ້າມາຮັບເອງ",
    shortLabel: "ມາຮັບ",
    variant: "secondary",
    hint: "ລູກຄ້າມາຮັບທີ່ຮ້ານ — ບໍ່ຕ້ອງໃສ່ຂົນສົ່ງ",
  },
  self_delivery: {
    label: "ຮ້ານໄປສົ່ງ",
    shortLabel: "ໄປສົ່ງ",
    variant: "default",
    hint: "ຮ້ານໄປສົ່ງໃຫ້ລູກຄ້າ — ກະລຸນາໃສ່ທີ່ຢູ່",
  },
};

export function deliveryMethodLabel(method: DeliveryMethod): string {
  return DELIVERY_METHOD_META[method]?.label ?? method;
}

export const PAYMENT_TYPE_META: Record<
  PaymentType,
  { label: string; shortLabel: string; variant: BadgeVariant }
> = {
  prepaid: {
    label: "ຊຳລະກ່ອນ",
    shortLabel: "ຊຳລະກ່ອນ",
    variant: "success",
  },
  cod: {
    label: "COD — ຈ່າຍເມື່ອຮັບ",
    shortLabel: "COD",
    variant: "secondary",
  },
};

export function paymentTypeLabel(type: PaymentType): string {
  return PAYMENT_TYPE_META[type]?.label ?? type;
}

/** ປ້າຍສະຖານະປາຍທາງເມື່ອຍ້ອນກັບ */
export function revertStatusDescription(
  from: OrderStatus,
  to: OrderStatus,
): string {
  const fromLabel = orderStatusLabel(from);
  const toLabel = orderStatusLabel(to);
  if (from === "paid" && to === "draft") {
    return `${fromLabel} → ${toLabel} — ຈະຄືນສະຕັອກ`;
  }
  if (from === "returned") {
    return `${fromLabel} → ${toLabel} — ຈະຫັກສະຕັອກອີກຄັ້ງ ແລະ ລຶບລາຍຈ່າຍຕີກັບ (ຖ້າມີ)`;
  }
  if (from === "picked_up" && (to === "shipped" || to === "delivered")) {
    return `${fromLabel} → ${toLabel} — COD ຈະລຶບ paid_at`;
  }
  return `${fromLabel} → ${toLabel}`;
}

/** ສະຫຼຸບວິທີຮັບ/ສົ່ງ ສຳລັບ UI ແລະ copy ແຊດ */
export function formatDeliverySummary(order: {
  deliveryMethod: DeliveryMethod;
  shippingCarrier: string | null;
  shippingBranch: string | null;
}): string {
  const method = order.deliveryMethod;
  if (method === "pickup") {
    return "ຮັບເອງທີ່ຮ້ານ";
  }
  if (method === "self_delivery") {
    return "ຮ້ານໄປສົ່ງ";
  }
  const carrier = order.shippingCarrier?.trim();
  if (!carrier) return deliveryMethodLabel("carrier");
  const branch = order.shippingBranch?.trim();
  return branch ? `${carrier} — ສາຂາ ${branch}` : carrier;
}
